import { useMemo, useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import { Button, Field, SelectField, StatusBadge, type Column } from "@fawkes/ui";

import { formatDate, todayIso } from "../i18n/format";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

// Crematorium — de werklijst: wat moet er wanneer gebeuren. Alle nog openstaande handelingen over
// alle lopende dossiers heen, vroegste vervaldatum eerst.
//
// De regels komen uit dezelfde ingestelde handelingen als het dossier zelf: het scherm Handelingen
// bepaalt wat er hoeveel dagen na welk moment moet gebeuren, en de api rekent dat per dossier uit.
// Er is dus niets om hier apart bij te houden — afvinken is de enige schrijfactie.
//
// De url is RELATIEF ("crematorium/werklijst") — de dataProvider zet er één keer /api voor.

interface WerklijstRow {
  dossierId: string;
  dossierNummer: string | null;
  dierNaam: string | null;
  diersoort: string | null;
  eigenaarNaam: string | null;
  verwerkingsvorm: string | null;
  status: string | null;
  handelingId: string;
  handeling: string;
  verplicht: boolean;
  document: string | null;
  verwachtOp: string | null;
  uitgevoerdOp: string | null;
  uitgevoerdDoor: string | null;
  opmerking: string | null;
}

/** Te laat, vandaag, of nog even — de enige drie dingen die je in een werklijst wilt zien. */
type Termijn = "verlopen" | "vandaag" | "komend" | "onbepaald";

function termijnVan(verwachtOp: string | null, vandaag: string): Termijn {
  if (!verwachtOp) return "onbepaald";
  if (verwachtOp < vandaag) return "verlopen";
  if (verwachtOp === vandaag) return "vandaag";
  return "komend";
}

const TERMIJN_TOON: Record<Termijn, "danger" | "warning" | "neutral"> = {
  verlopen: "danger",
  vandaag: "warning",
  komend: "neutral",
  onbepaald: "neutral",
};

export function CrematoriumWerklijst() {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const vandaag = todayIso();

  const { data, isLoading, isError, refetch } = useCustom<WerklijstRow[]>({
    url: "crematorium/werklijst",
    method: "get",
  });
  const alle = useMemo(() => data?.data ?? [], [data]);

  const [zoek, setZoek] = useState("");
  const [termijnFilter, setTermijnFilter] = useState<"" | Termijn>("");
  const [handelingFilter, setHandelingFilter] = useState("");

  // De namen van de handelingen zijn een instelling, dus de filterlijst komt uit de data zelf.
  const handelingNamen = useMemo(
    () => [...new Set(alle.map((r) => r.handeling))].sort((a, b) => a.localeCompare(b, locale)),
    [alle, locale],
  );

  const rijen = useMemo(() => {
    const naald = zoek.trim().toLowerCase();
    return alle.filter((r) => {
      if (termijnFilter && termijnVan(r.verwachtOp, vandaag) !== termijnFilter) return false;
      if (handelingFilter && r.handeling !== handelingFilter) return false;
      if (!naald) return true;
      return [r.dossierNummer, r.dierNaam, r.eigenaarNaam, r.handeling]
        .filter(Boolean)
        .some((veld) => (veld as string).toLowerCase().includes(naald));
    });
  }, [alle, zoek, termijnFilter, handelingFilter, vandaag]);

  const { mutate, isLoading: bezig } = useCustomMutation();
  const [notice, setNotice] = useState<"forbidden" | "error" | null>(null);

  const rondAf = (row: WerklijstRow) => {
    setNotice(null);
    mutate(
      {
        url: `crematorium/dossiers/${row.dossierId}/handelingen/${row.handelingId}/afronden`,
        method: "post",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => refetch(),
        onError: (err) =>
          setNotice((err as { statusCode?: number }).statusCode === 403 ? "forbidden" : "error"),
      },
    );
  };

  const columns: Column<WerklijstRow>[] = [
    {
      header: t("crematoriumWerklijst.col.verwachtOp"),
      cell: (r) => {
        const termijn = termijnVan(r.verwachtOp, vandaag);
        return (
          <StatusBadge tone={TERMIJN_TOON[termijn]}>
            {r.verwachtOp
              ? formatDate(r.verwachtOp, locale)
              : t("crematoriumWerklijst.termijn.onbepaald")}
          </StatusBadge>
        );
      },
    },
    { header: t("crematoriumWerklijst.col.handeling"), cell: (r) => <strong>{r.handeling}</strong> },
    {
      header: t("crematoriumWerklijst.col.dossier"),
      cell: (r) => (
        <>
          <div>{r.dierNaam ?? t("crematoriumWerklijst.dier.onbekend")}</div>
          <div className="fui-muted">{r.dossierNummer ?? "—"}</div>
        </>
      ),
    },
    { header: t("crematoriumWerklijst.col.eigenaar"), cell: (r) => r.eigenaarNaam ?? "—" },
    {
      header: t("crematoriumWerklijst.col.vorm"),
      cell: (r) => (r.verwerkingsvorm ? t(`crematoriumTarief.vorm.${r.verwerkingsvorm}`) : "—"),
    },
    {
      header: t("crematoriumWerklijst.col.verplicht"),
      cell: (r) =>
        r.verplicht ? (
          <StatusBadge tone="info">{t("crematoriumHandeling.verplicht.ja")}</StatusBadge>
        ) : (
          <span className="fui-muted">{t("crematoriumHandeling.verplicht.nee")}</span>
        ),
    },
    {
      header: t("crematoriumTarief.col.acties"),
      align: "right",
      cell: (r) => (
        <Button variant="secondary" disabled={bezig} onClick={() => rondAf(r)}>
          {t("crematoriumWerklijst.action.afronden")}
        </Button>
      ),
    },
  ];

  return (
    <div className="asiel-tarief__panel">
      <div className="asiel-tarief__toolbar">
        <Field
          label={t("crematoriumWerklijst.search.label")}
          value={zoek}
          onChange={(e) => setZoek(e.target.value)}
        />
        <SelectField
          label={t("crematoriumWerklijst.filter.termijn")}
          value={termijnFilter}
          onChange={(e) => setTermijnFilter(e.target.value as "" | Termijn)}
          options={[
            { value: "", label: t("crematoriumPlanning.filter.all") },
            { value: "verlopen", label: t("crematoriumWerklijst.termijn.verlopen") },
            { value: "vandaag", label: t("crematoriumWerklijst.termijn.vandaag") },
            { value: "komend", label: t("crematoriumWerklijst.termijn.komend") },
            { value: "onbepaald", label: t("crematoriumWerklijst.termijn.onbepaald") },
          ]}
        />
        <SelectField
          label={t("crematoriumWerklijst.filter.handeling")}
          value={handelingFilter}
          onChange={(e) => setHandelingFilter(e.target.value)}
          options={[
            { value: "", label: t("crematoriumWerklijst.filter.alleHandelingen") },
            ...handelingNamen.map((naam) => ({ value: naam, label: naam })),
          ]}
        />
        <span className="asiel-tarief__toolbar-spacer" />
        <Button variant="secondary" onClick={() => refetch()}>
          {t("crematoriumPlanning.action.refresh")}
        </Button>
      </div>

      {isError && (
        <div className="asiel-tarief__notice asiel-tarief__notice--error">
          {t("crematoriumPlanning.load.error")}
        </div>
      )}
      {notice && (
        <div className="asiel-tarief__notice asiel-tarief__notice--error">
          {t(`crematoriumTarief.notice.${notice}`)}
        </div>
      )}

      <IndeelbareTabel<WerklijstRow> tabelKey="crematorium-werklijst"
        columns={columns}
        rows={rijen}
        getRowKey={(r) => `${r.dossierId}:${r.handelingId}`}
        empty={isLoading ? t("common.loading") : t("crematoriumWerklijst.empty")}
      />

      <div className="crem-note">
        <span>{t("crematoriumWerklijst.uitleg")}</span>
      </div>
    </div>
  );
}
